import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  BulkCSVUpdateJob,
  BulkCSVUpdateJobResult,
  BulkCSVUpdateMappings,
  BulkCSVUpdateProvider,
  BulkCSVUpdateStatus,
  CreateCSVJobRequest,
} from 'models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  S9APIBulkCSVUpdateJob,
  S9APIBulkCSVUpdateJobResult,
  S9ApiBulkCSVUpdateMappings,
  S9ApiBulkCSVUpdateStatusDefinition,
  createBulkCSVUpdateJobFromApi,
  createBulkCSVUpdateMappingsFromApi,
  createBulkCSVUpdateResultFromApi,
  createBulkCSVUpdateStatusFromApi,
} from '../models/bulk-csv-update';

import { Square9ApiConfig } from './square9-api-config.model';
import { SQUARE9_API_CONFIG } from './square9-api-config.token';

/** Bulk CSV Update Service. */
@Injectable({
  providedIn: 'root',
})
export class Square9ApiBulkCSVUpdateService implements BulkCSVUpdateProvider {
  private basePath: string;

  constructor(
    @Inject(SQUARE9_API_CONFIG) private config: Square9ApiConfig,
    private http: HttpClient
  ) {
    this.config.apiUrl$.subscribe(
      (apiUrl) => (this.basePath = `${apiUrl}/bulkcsvupdate`)
    );
  }

  /** @inheritdoc */
  cancelJob(jobId: string): Observable<void> {
    return this.http.put<void>(
      `${this.basePath}/cancel`,
      {},
      {
        params: new HttpParams().set('jobId', jobId),
      }
    );
  }

  /** @inheritdoc */
  deleteJob(jobId: string): Observable<void> {
    return this.http.delete<void>(`${this.basePath}`, {
      params: new HttpParams().set('jobId', jobId),
    });
  }

  /** @inheritdoc */
  downloadJobResult(jobId: string): Observable<void> {
    return this.http
      .get<any>(`${this.basePath}/resultpackage`, {
        params: new HttpParams().set('jobId', jobId),
        responseType: 'blob' as 'json',
      })
      .pipe(
        map((response) => {
          const dataType = response.type;
          const binaryData = [];
          binaryData.push(response);
          const downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(
            new Blob(binaryData, {
              type: dataType,
            })
          );
          document.body.append(downloadLink);
          downloadLink.click();
          downloadLink.remove();
        })
      );
  }

  /** @inheritdoc */
  getCreateJobRequest(
    databaseId: number,
    columnDelimiter: string,
    stringDelimiter: string,
    containsColumnHeaders: boolean,
    token: string
  ): CreateCSVJobRequest {
    const httpParameters = new HttpParams()
      .set('databaseId', databaseId.toString())
      .set('columnDelimiter', columnDelimiter)
      .set('stringDelimiter', stringDelimiter)
      .set('containsColumnHeaders', containsColumnHeaders.toString());
    return {
      url: `${this.basePath}?${httpParameters.toString()}`,
      method: 'POST',
      headers: {
        'AUTH-TOKEN': token,
      },
    };
  }

  /** @inheritdoc */
  getJob(jobId: string): Observable<BulkCSVUpdateJob> {
    return this.http
      .get<S9APIBulkCSVUpdateJob>(`${this.basePath}/job`, {
        params: new HttpParams().set('jobId', jobId),
      })
      .pipe(map((apiJob) => createBulkCSVUpdateJobFromApi(apiJob)));
  }

  /** @inheritdoc */
  getJobColumnMappings(jobId: string): Observable<BulkCSVUpdateMappings> {
    return this.http
      .get<S9ApiBulkCSVUpdateMappings>(`${this.basePath}/columnmappings`, {
        params: new HttpParams().set('jobId', jobId),
      })
      .pipe(
        map((apiColumnMappings) =>
          createBulkCSVUpdateMappingsFromApi(apiColumnMappings)
        )
      );
  }

  /** @inheritdoc */
  getJobResult(jobId: string): Observable<BulkCSVUpdateJobResult> {
    return this.http
      .get<S9APIBulkCSVUpdateJobResult>(`${this.basePath}/result`, {
        params: new HttpParams().set('jobId', jobId),
      })
      .pipe(
        map((apiJobResult) => createBulkCSVUpdateResultFromApi(apiJobResult))
      );
  }

  /** @inheritdoc */
  getJobStatus(jobId: string): Observable<BulkCSVUpdateStatus> {
    return this.http
      .get<S9ApiBulkCSVUpdateStatusDefinition>(`${this.basePath}/status`, {
        params: new HttpParams().set('jobId', jobId),
      })
      .pipe(map((apiJob) => createBulkCSVUpdateStatusFromApi(apiJob)));
  }

  /** @inheritdoc */
  getJobs(): Observable<BulkCSVUpdateJob[]> {
    return this.http
      .get<S9APIBulkCSVUpdateJob[]>(`${this.basePath}`)
      .pipe(map((jobs) => jobs.map(createBulkCSVUpdateJobFromApi)));
  }

  /** @inheritdoc */
  startUpdate(
    jobId: string,
    columnMappings: BulkCSVUpdateMappings,
    overwriteSystemFields: boolean
  ): Observable<void> {
    return this.http.put<void>(`${this.basePath}/start`, columnMappings, {
      params: new HttpParams()
        .set('jobId', jobId)
        .set('overwriteSystemFields', overwriteSystemFields),
    });
  }
}
